import { CallBadge } from "@/components/shared/CallBadge";
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { MapPin, ChevronRight, Store, ArrowLeft } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATE_NAMES = {
  AL:"Alabama",AK:"Alaska",AZ:"Arizona",AR:"Arkansas",CA:"California",CO:"Colorado",CT:"Connecticut",
  DE:"Delaware",FL:"Florida",GA:"Georgia",HI:"Hawaii",ID:"Idaho",IL:"Illinois",IN:"Indiana",IA:"Iowa",
  KS:"Kansas",KY:"Kentucky",LA:"Louisiana",ME:"Maine",MD:"Maryland",MA:"Massachusetts",MI:"Michigan",
  MN:"Minnesota",MS:"Mississippi",MO:"Missouri",MT:"Montana",NE:"Nebraska",NV:"Nevada",NH:"New Hampshire",
  NJ:"New Jersey",NM:"New Mexico",NY:"New York",NC:"North Carolina",ND:"North Dakota",OH:"Ohio",OK:"Oklahoma",
  OR:"Oregon",PA:"Pennsylvania",RI:"Rhode Island",SC:"South Carolina",SD:"South Dakota",TN:"Tennessee",
  TX:"Texas",UT:"Utah",VT:"Vermont",VA:"Virginia",WA:"Washington",WV:"West Virginia",WI:"Wisconsin",WY:"Wyoming",
  DC:"District of Columbia",
};

export default function LocationsByCityPage() {
  const { state, city } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const stateName = STATE_NAMES[state?.toUpperCase()] || state?.toUpperCase() || "";

  useEffect(() => {
    fetch(`${API_URL}/api/locations/by-state/${state}/${city}`)
      .then(r => r.json())
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [state, city]);

  const locations = data?.locations || [];
  const cityDisplay = data?.city || city?.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || "";
  const validLocs = locations.filter(l => l.latitude && l.longitude);
  const center = validLocs.length
    ? [validLocs.reduce((s, l) => s + l.latitude, 0) / validLocs.length, validLocs.reduce((s, l) => s + l.longitude, 0) / validLocs.length]
    : [39.8, -98.5];

  const seoTitle = `Total Wireless Stores in ${cityDisplay}, ${state?.toUpperCase()} | Prime Media Wireless`;
  const seoDesc = `Total Wireless / Prime Media Wireless authorized dealer locations in ${cityDisplay}, ${stateName}. Visit a store near you for phones, plans, accessories, and repair services.`;
  const canonicalUrl = `https://pmw-coming-soon.preview.emergentagent.com/locations/${state}/${city}`;

  return (
    <PublicLayout>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {/* Hero */}
      <section className="bg-navy py-16" data-testid="city-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-4">
            <Link to="/locations" className="hover:text-teal transition-colors">All Locations</Link>
            <span>/</span>
            <Link to={`/locations/${state}`} className="hover:text-teal transition-colors">{stateName}</Link>
            <span>/</span>
            <span className="text-teal">{cityDisplay}</span>
          </div>
          <h1 className="font-heading font-bold text-4xl lg:text-5xl text-white mb-3" data-testid="city-title">
            Total Wireless Stores in {cityDisplay}, {state?.toUpperCase()}
          </h1>
          <p className="text-slate-300 text-lg font-body">
            {data?.total || 0} Total Wireless / Prime Media Wireless {data?.total === 1 ? "store" : "stores"} in {cityDisplay}, {stateName}.
          </p>
        </div>
      </section>

      {loading ? (
        <div className="py-20 text-center text-slate-400">Loading...</div>
      ) : locations.length === 0 ? (
        <div className="py-20 text-center">
          <Store className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-heading font-semibold text-navy mb-2">No Stores Found</h2>
          <p className="text-slate-500">We don't have any locations in {cityDisplay}, {stateName} yet.</p>
          <Link to={`/locations/${state}`} className="text-teal hover:underline mt-4 inline-block">View all {stateName} stores</Link>
        </div>
      ) : (
        <>
          {/* Map */}
          {validLocs.length > 0 && (
            <section className="py-8 bg-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="h-[350px] rounded-xl overflow-hidden border border-slate-200">
                  <MapContainer center={center} zoom={12} className="h-full w-full">
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                    {validLocs.map(loc => (
                      <Marker key={loc.id} position={[loc.latitude, loc.longitude]}>
                        <Popup>
                          <strong>{loc.name}</strong><br />
                          {loc.address}, {loc.city}, {loc.state}<br />
                          <Link to={`/locations/${loc.slug || loc.id}`} className="text-teal text-sm">View Details</Link>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>
              </div>
            </section>
          )}

          {/* Location Cards */}
          <section className="py-10 bg-slate-50" data-testid="city-locations">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="font-heading font-bold text-xl text-navy mb-6">
                Stores in {cityDisplay}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {locations.map(loc => (
                  <Link key={loc.id} to={`/locations/${loc.slug || loc.id}`}
                    className="group bg-white border border-slate-200 rounded-xl p-5 hover:border-teal hover:shadow-md transition-all"
                    data-testid={`store-card-${loc.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-teal" />
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-teal transition-colors" />
                    </div>
                    <h3 className="font-heading font-semibold text-navy text-base mb-1">{loc.name}</h3>
                    <p className="text-slate-500 text-sm">{loc.address}</p>
                    <p className="text-slate-500 text-sm">{loc.city}, {loc.state} {loc.zip_code}</p>
                    <CallBadge phone={loc.phone} className="mt-2" testId={`call-badge-${loc.id}`} />
                    {loc.services?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {loc.services.slice(0, 3).map(s => (
                          <span key={s} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{s}</span>
                        ))}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}
    </PublicLayout>
  );
}
